import { DatePeriod, TimeOfDay } from "@greeter/date";
import { Log } from "@greeter/log";
import { ShallowDatePeriod } from "./types";
import { SpecialOpeningHours, Venue, WeeklyOpeningHours } from "@greeter/core";
import { addDays, subDays } from "date-fns";
import { ErrorHint, WarningHint } from "../ErrorBorder";
import { assert } from "@greeter/assert";

function ValidationActionButton({
  style,
  children,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      style={{
        marginLeft: 5,
        padding: 5,
        borderRadius: 3,
        backgroundColor: "var(--gm-color-primary-highlight)",
        color: "var(--gm-color-sensible-white)",
        ...style,
      }}
      {...props}
    >
      {children ? children : "Fiks"}
    </button>
  );
}

export type ValidateDatePeriodMatchesOpeningHoursResult =
  | {
      type: "start-doesnt-match";
      data: { current: Date; target: Date };
    }
  | {
      type: "end-doesnt-match";
      data: { current: Date; target: Date };
    }
  | {
      type: "start-and-end-doesnt-match";
      data: { current: DatePeriod; target: DatePeriod };
    }
  | { type: "valid" };

/**
 * Helper to validate if a special price does not overlap with opening hours.
 */
export function validateDatePeriodMatchesOpeningHours(
  datePeriod: DatePeriod,
  openingHours: Array<DatePeriod>
): ValidateDatePeriodMatchesOpeningHoursResult {
  let start: DatePeriod | undefined;
  let end: DatePeriod | undefined;

  /**
   * Might span over multiple periods, and so we try to identify both.
   */
  for (const oh of openingHours) {
    if (oh.isWithin(datePeriod.from)) {
      start = oh;
    }
    if (oh.isWithin(datePeriod.to)) {
      end = oh;
    }
    if (start && end) break;
  }

  Log.debug("[validateDatePeriodMatchesOpeningHours]", {
    start,
    end,
    datePeriod,
  });

  if (start && end && datePeriod.from > start.from && datePeriod.to < end.to) {
    return {
      type: "start-and-end-doesnt-match",
      data: {
        current: datePeriod,
        target: new DatePeriod({ from: start.from, to: end.to }),
      },
    };
  }

  if (start && datePeriod.from > start.from) {
    return {
      type: "start-doesnt-match",
      data: { current: datePeriod.from, target: start.from },
    };
  }

  if (end && datePeriod.to < end.to) {
    return {
      type: "end-doesnt-match",
      data: { current: datePeriod.to, target: end.to },
    };
  }

  /**
   * Starts and ends on days where
   * there is no opening hours which must mean that
   * it overlaps all relevant opening hours.
   */
  return { type: "valid" };
}

type Target = "from" | "to" | "both";

export type ValidationResult = {
  valid: boolean;
  target?: Target;
  level?: "error" | "warn";
  reason?: React.ReactNode;
};

export type ValidateFn = (datePeriod: ShallowDatePeriod) => ValidationResult;

export function validateFromIsBeforeTo(value: ShallowDatePeriod) {
  if (value.from > value.to) {
    return {
      valid: false,
      target: "to" as Target,
      reason: <ErrorHint>Til feltet kan ikke være før fra feltet</ErrorHint>,
    };
  }

  return { valid: true };
}

export function setupValidateOpeningsHoursMatch({
  highest,
  lowest,
  openingHours: oh,
  specialOpeningHours: soh,
  onMatchFrom,
  onMatchTo,
}: {
  lowest?: Date;
  highest?: Date;
  openingHours: WeeklyOpeningHours;
  specialOpeningHours?: Array<SpecialOpeningHours>;
  onMatchFrom?: (date: Date) => void;
  onMatchTo?: (date: Date) => void;
}): ValidateFn {
  return function validateOpeningsHoursMatch(p) {
    Log.debug(`[validateOpeningsHoursMatch] trying to validate`, { p });
    const openingHours = Venue.createDatePeriodsFromOpeningHours({
      openingHours: oh,
      specialOpeningHours: soh ?? [],
      range:
        highest && lowest
          ? new DatePeriod({
              from: lowest,
              to: addDays(highest, 7),
            })
          : 30,
      now: subDays(lowest ?? new Date(), 5) ?? p.from,
    });
    try {
      const validation = validateDatePeriodMatchesOpeningHours(
        new DatePeriod(p),
        openingHours.map((ldp) => ldp.toStrict())
      );

      return validation.type === "valid"
        ? { valid: true }
        : validation.type === "start-doesnt-match"
        ? {
            valid: false,
            target: "from",
            level: "warn",
            reason: (
              <div style={{ paddingTop: 5 }}>
                <WarningHint>
                  Starten matcher ikke åbningstid, som er kl.{" "}
                  {TimeOfDay.fromDate(validation.data.target).toString()}
                </WarningHint>
                <ValidationActionButton
                  onClick={() => {
                    assert(validation);
                    assert(validation?.type === "start-doesnt-match");
                    onMatchFrom?.(validation?.data?.target);
                  }}
                >
                  Skift til{" "}
                  {TimeOfDay.fromDate(validation.data.target).toString()}
                </ValidationActionButton>
              </div>
            ),
          }
        : validation.type === "end-doesnt-match"
        ? {
            valid: false,
            target: "to",
            level: "warn",
            reason: (
              <div style={{ paddingTop: 5 }}>
                <WarningHint>
                  Slutning matcher ikke som er kl.{" "}
                  {TimeOfDay.fromDate(validation.data.target).toString()}
                </WarningHint>
                <ValidationActionButton
                  onClick={() => {
                    assert(validation?.type === "end-doesnt-match");
                    onMatchTo?.(validation.data.target);
                  }}
                >
                  Skift til{" "}
                  {TimeOfDay.fromDate(validation.data.target).toString()}
                </ValidationActionButton>
              </div>
            ),
          }
        : {
            valid: false,
            target: "both",
            level: "warn",
            reason: (
              <div style={{ paddingTop: 5 }}>
                <WarningHint style={{ paddingLeft: 0 }}>
                  Start og slut matcher ikke åbningstider som er kl.
                  {TimeOfDay.fromDate(
                    validation.data.target.from
                  ).toString()} -{" "}
                  {TimeOfDay.fromDate(validation.data.target.to).toString()}
                </WarningHint>
                <ValidationActionButton
                  onClick={() => {
                    assert(validation?.type === "start-and-end-doesnt-match");
                    onMatchFrom?.(validation.data.target.from);
                    onMatchTo?.(validation.data.target.to);
                  }}
                >
                  Skift til{" "}
                  {TimeOfDay.fromDate(validation.data.target.from).toString()} -{" "}
                  {TimeOfDay.fromDate(validation.data.target.to).toString()}
                </ValidationActionButton>
              </div>
            ),
          };
    } catch (error) {
      Log.error("failed to validate date period against opening hours", error);
    }
    return { valid: true };
  };
}
